import React, { useState, useMemo } from 'react';
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { useHeaderSearch } from "@/hooks/useHeaderSearch";
import { trpcClient, trpc } from '@/lib/trpc';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { useTableSelection } from '@/hooks/useTableSelection';
import { FloatingBulkActionBar } from '@/components/ui/FloatingBulkActionBar';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import { exportToExcel } from '@/lib/excel.utils';

const EXPORT_COLUMNS = [
  { key: 'materialCode', label: 'Mã vật tư', width: 16 },
  { key: 'materialName', label: 'Tên vật tư', width: 36 },
  { key: 'unit', label: 'ĐVT', width: 8 },
  { key: 'qtyRequired', label: 'Tổng cần (LSX)', width: 14, format: 'number' as const },
  { key: 'qtyAvailable', label: 'Tồn kho khả dụng', width: 14, format: 'number' as const },
  { key: 'shortage', label: 'Thiếu hụt', width: 12, format: 'number' as const },
  { key: 'productionOrders', label: 'LSX liên quan', width: 40, exportFn: (r: any) => (r.productionOrders || []).join(', ') },
];

export const MrpDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [isCalculating, setIsCalculating] = useState(false);
  const { headerSearch } = useHeaderSearch();

  // Fetch shortages
  const { data: shortages, isLoading, refetch } = useQuery({
    ...trpc.mrp.calculateRequirements.queryOptions({}),
    enabled: true, // Auto fetch initially, but user can click to recalculate
  });

  // Use the global hook for selection
  const {
    selectedIds,
    toggleSelection,
    toggleAll,
    clearSelection,
    isAllSelected,
  } = useTableSelection(shortages?.map((s) => ({ id: s.materialId })) || []);

  const isSelected = (id: string) => selectedIds.includes(id);

  const filteredShortages = useMemo(() => {
    if (!shortages) return [];
    if (!headerSearch) return shortages;
    const term = headerSearch.toLowerCase();
    return shortages.filter(s =>
      s.materialCode?.toLowerCase().includes(term) ||
      s.materialName?.toLowerCase().includes(term)
    );
  }, [shortages, headerSearch]);

  const handleCalculate = async () => {
    setIsCalculating(true);
    await refetch();
    setIsCalculating(false);
    toast.success('Đã tính toán xong nhu cầu vật tư');
  };

  const handleCreatePurchaseRequests = async () => {
    if (selectedIds.length === 0) return;

    // Lọc ra các vật tư được chọn
    const selectedItems = shortages?.filter(s => selectedIds.includes(s.materialId)) || [];
    
    // Prepare items array
    const prItems = selectedItems.map(item => ({
      materialId: item.materialId,
      qtyToOrder: item.shortage, // Order the exact shortage amount by default
      planIds: item.planIds,
      productionOrders: item.productionOrders,
    }));

    try {
      // Call mutation
      const result = await trpcClient.mrp.generatePurchaseRequests.mutate({ items: prItems });
      toast.success('Đã sinh Yêu cầu mua hàng (PR) thành công');
      clearSelection();
      
      // Chuyển hướng sang Form PO kèm các PR ID
      navigate({
        to: '/purchasing/create',
        search: { prIds: result.prIds }
      });
    } catch (error: any) {
      toast.error('Lỗi khi sinh Yêu cầu mua hàng: ' + error.message);
    }
  };

  const selectedCount = selectedIds.length;

  const exportRows = (rows: any[]) =>
    exportToExcel(rows, EXPORT_COLUMNS, 'nhu_cau_vat_tu_mrp', { sheetName: 'MRP' });

  const actions = (
    <div className="flex items-center gap-2">
    <Button variant="outline" onClick={() => exportRows(filteredShortages)} disabled={!filteredShortages.length}>
      <Download className="h-4 w-4 mr-1.5" /> Xuất Excel
    </Button>
    <Button onClick={handleCalculate} disabled={isCalculating || isLoading} className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-sm">
      {isCalculating ? 'Đang tính toán...' : 'Tính MRP (F5)'}
    </Button>
    </div>
  );

  return (
    <StandardDetailLayout
      title="Tính Nhu Cầu Vật Tư"
      breadcrumbs={[
        { label: "Cung ứng & MRP", to: "/planning/mrp" },
        { label: "Tính MRP" },
      ]}
      actions={actions}
    >
      <div className="p-4 flex-1 flex flex-col gap-4 overflow-hidden h-full">{/* Single-Row Inline Metrics Bar */}
        <div className="flex items-center gap-4 bg-white p-2 px-4 rounded-lg border shadow-sm shrink-0">
          <div className="text-sm font-medium text-slate-500">Tổng quan MRP:</div>
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
            {shortages?.length || 0} mã vật tư thiếu
          </Badge>
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            {shortages?.filter(s => s.shortage > 0).reduce((acc, curr) => acc + curr.shortage, 0).toLocaleString() || 0} tổng SL thiếu
          </Badge>
        </div>

        {/* DataGrid with Anti-Ghosting Solid Sticky Columns */}
        <div className="bg-white rounded-lg border shadow-sm flex-1 overflow-auto max-h-[calc(100vh-13rem)] isolate erp-table-wrapper">
          <Table>
            <TableHeader className="bg-slate-50 sticky top-0 z-10 shadow-[0_1px_0_0_#e2e8f0]">
              <TableRow>
                <TableHead className="w-12 text-center bg-white sticky left-0 z-20 !important">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={(checked) => toggleAll(checked as boolean)}
                  />
                </TableHead>
                <TableHead className="min-w-[120px] bg-white sticky left-[48px] z-20 !important">
                  Mã Vật Tư
                </TableHead>
                <TableHead className="min-w-[250px] bg-white sticky left-[168px] z-20 !important">
                  Tên Vật Tư
                </TableHead>
                <TableHead className="w-[100px]">ĐVT</TableHead>
                <TableHead className="text-right text-blue-600 font-semibold w-[150px]">Tổng Cần (Lệnh SX)</TableHead>
                <TableHead className="text-right text-green-600 font-semibold w-[150px]">Tồn Kho (Khả Dụng)</TableHead>
                <TableHead className="text-right text-red-600 font-bold w-[150px]">Thiếu Hụt</TableHead>
                <TableHead className="min-w-[200px]">Liên Kết (LSX)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    Đang tải dữ liệu...
                  </TableCell>
                </TableRow>
              ) : shortages?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    Tuyệt vời! Không có Lệnh SX nào thiếu vật tư.
                  </TableCell>
                </TableRow>
              ) : (
                filteredShortages?.map((item) => (
                  <TableRow
                    key={item.materialId}
                    className={`cursor-pointer hover:bg-slate-50 ${isSelected(item.materialId) ? 'bg-blue-50/50' : ''}`}
                    onClick={() => {
                      if (window.getSelection()?.toString().trim()) return;
                      navigate({ to: '/materials/$materialId', params: { materialId: item.materialId } });
                    }}
                  >
                    <TableCell
                      className={`text-center bg-white sticky left-0 z-10 !important ${isSelected(item.materialId) ? '!bg-blue-50' : ''}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected(item.materialId)}
                        onCheckedChange={() => toggleSelection(item.materialId)}
                      />
                    </TableCell>
                    <TableCell className={`font-medium text-blue-600 bg-white sticky left-[48px] z-10 !important ${isSelected(item.materialId) ? '!bg-blue-50' : ''}`}>
                      {item.materialCode}
                    </TableCell>
                    <TableCell className={`bg-white sticky left-[168px] z-10 !important ${isSelected(item.materialId) ? '!bg-blue-50' : ''}`}>
                      {item.materialName}
                    </TableCell>
                    <TableCell>{item.unit}</TableCell>
                    <TableCell className="text-right font-medium">
                      {item.qtyRequired.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {item.qtyAvailable.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-right font-bold text-red-600">
                      {item.shortage > 0 ? item.shortage.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500 max-w-[250px] truncate" title={item.productionOrders.join(', ')}>
                      {item.productionOrders.join(', ')}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Floating Bulk Action Bar (Rule #9) */}
      <FloatingBulkActionBar
        selectedCount={selectedCount}
        onClear={clearSelection}
      >
        <Button
          size="sm"
          variant="outline"
          onClick={() => exportRows(filteredShortages.filter((s) => selectedIds.includes(s.materialId)))}
        >
          <Download className="h-3.5 w-3.5 mr-1" /> Xuất Excel
        </Button>
        <Button size="sm" onClick={handleCreatePurchaseRequests}>
          Tạo Yêu Cầu Mua (PR) & Chuyển sang PO
        </Button>
      </FloatingBulkActionBar>
    </StandardDetailLayout>
  );
};

export default MrpDashboard;
